import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCheck } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { NotificationItem } from './NotificationItem'
import { getNotificationAction } from '@/utils/notificationActions'
import {
  useRecentNotifications,
  useMarkNotificationRead,
  useMarkAllRead,
} from '@/hooks/useNotifications'
import type { NotificationRead } from '@/types/notification'

interface NotificationPopoverProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
}

export function NotificationPopover({
  open,
  onOpenChange,
  children,
}: NotificationPopoverProps) {
  const navigate = useNavigate()
  const { data } = useRecentNotifications()
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllRead()

  const notifications = data?.data ?? []
  const unreadCount = data?.unread_count ?? 0

  const handleNotificationClick = useCallback(
    (notification: NotificationRead) => {
      if (!notification.is_read) {
        markRead.mutate({ id: notification.id, is_read: true })
      }
      onOpenChange(false)
      const action = getNotificationAction(notification)
      if (action) {
        navigate(action.url, { state: action.state })
      } else if (notification.action_url) {
        navigate(notification.action_url)
      }
    },
    [markRead, navigate, onOpenChange]
  )

  const handleActionClick = useCallback(
    (
      notification: NotificationRead,
      url: string,
      state?: Record<string, unknown>
    ) => {
      if (!notification.is_read) {
        markRead.mutate({ id: notification.id, is_read: true })
      }
      onOpenChange(false)
      navigate(url, { state })
    },
    [markRead, navigate, onOpenChange]
  )

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[380px] p-0"
        onOpenAutoFocus={e => e.preventDefault()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">
            Notifications
          </h3>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-1 text-xs"
            disabled={unreadCount === 0}
            onClick={() => markAllRead.mutate()}
          >
            <CheckCheck className="mr-1 h-3 w-3" />
            Mark all as read
          </Button>
        </div>

        {/* Body */}
        <div className="max-h-[400px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <CheckCheck className="mb-2 h-8 w-8" />
              <p className="text-sm">You're all caught up!</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  compact
                  onClick={handleNotificationClick}
                  onActionClick={handleActionClick}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-4 py-2">
          <button
            type="button"
            onClick={() => {
              onOpenChange(false)
              navigate('/notifications')
            }}
            className="w-full cursor-pointer text-center text-xs font-medium text-primary hover:underline"
          >
            View all notifications
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
